import {getAnalytics, logEvent} from 'firebase/analytics';
import {initializeApp} from 'firebase/app';
import {
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  getAuth,
  isSignInWithEmailLink,
  onAuthStateChanged,
  sendSignInLinkToEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithEmailLink,
  signOut,
  updatePassword,
  User,
} from 'firebase/auth';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import {BillingPeriod} from '../types/billing';
import {initializeClickTracker} from './analytics/click-tracker';
import {logger} from './logger';

// Types for click tracker
export interface ClickTracker {
  disable: () => void;
  enable: () => void;
  trackClick: (element: string, metadata?: Record<string, any>) => void;
  trackExerciseSettings: (exerciseType: string, settings: Record<string, any>) => void;
}

// Global variable to hold the active click tracker instance
let activeClickTracker: ClickTracker | null = null;

// Your Firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID, // Add measurement ID for Analytics
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
export const db = getFirestore(app);
export const analytics = getAnalytics(app); // Initialize Analytics

// Set persistence to LOCAL
setPersistence(auth, browserLocalPersistence)
  .then(() => {
    logger.info('Firebase Auth initialized with LOCAL persistence');
  })
  .catch(error => {
    logger.error('Error setting auth persistence', error);
  });

// Email link authentication settings
const actionCodeSettings = {
  // URL you want to redirect back to. The domain (www.example.com) for this
  // URL must be in the authorized domains list in the Firebase Console.
  url: `${window.location.origin}/login/confirm`,
  // This must be true
  handleCodeInApp: true,
};

// New email link authentication functions
export async function sendSignInLink(email: string) {
  try {
    logger.info(`Sending sign-in link to: ${email}`);
    await sendSignInLinkToEmail(auth, email, actionCodeSettings);

    // Save the email locally to use it for sign-in completion
    localStorage.setItem('emailForSignIn', email);
    logger.info('Sign-in link sent successfully');
    return true;
  } catch (error) {
    logger.error('Error sending sign-in link', error);
    throw error;
  }
}

export function checkIfSignInWithEmailLink(url: string = window.location.href) {
  return isSignInWithEmailLink(auth, url);
}

export async function completeSignInWithEmailLink(email: string, url: string = window.location.href) {
  try {
    logger.info(`Completing sign-in with email link for: ${email}`);
    const userCredential = await signInWithEmailLink(auth, email, url);

    // Clear email from storage
    localStorage.removeItem('emailForSignIn');

    // Check if user profile exists, if not, create one
    const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));

    if (!userDoc.exists()) {
      logger.info(`Creating new user profile for email link sign-in: ${userCredential.user.uid}`);
      // Create basic profile for new users
      const userProfile: UserProfile = {
        email,
        name: '',
        subscription: {
          plan: 'free',
          status: 'active',
        },
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await setDoc(doc(db, 'users', userCredential.user.uid), userProfile);
      logger.info('User profile created successfully');
    }

    logger.info('Email link sign-in completed successfully');
    return userCredential.user;
  } catch (error) {
    logger.error('Error completing sign-in with email link', error);
    throw error;
  }
}

export type UserSubscription = {
  plan: 'free' | 'pro';
  billing?: BillingPeriod;
  status: 'active' | 'cancelled' | 'past_due';
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  currentPeriodEnd?: Date;
};

export type OnboardingStep = {
  id: string;
  title: string;
  description: string;
  icon: string;
  completed: boolean;
  completedAt?: Date;
};

export type UserProfile = {
  email: string;
  name: string;
  subscription: UserSubscription;
  createdAt: Date;
  updatedAt: Date;
  onboarding?: {
    steps: {
      [key: string]: {
        completed: boolean;
        completedAt?: Date;
      };
    };
    lastUpdated: Date;
    showSetupGuide?: boolean;
  };
};

// Auth functions
export async function registerUser(email: string, password: string, profile: Partial<UserProfile>) {
  try {
    // Create user with Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    logger.info(`User created in Firebase Auth: ${user.uid}`);
    logger.info('Profile', profile);

    // Ensure subscription dates are properly formatted
    const subscription = profile.subscription || {
      plan: 'free',
      status: 'active',
    };

    // Make sure currentPeriodEnd is a proper Date object if it exists
    if (subscription.currentPeriodEnd) {
      if (typeof subscription.currentPeriodEnd === 'string') {
        subscription.currentPeriodEnd = new Date(subscription.currentPeriodEnd);
      } else if (
        typeof subscription.currentPeriodEnd === 'object' &&
        subscription.currentPeriodEnd !== null &&
        'toDate' in subscription.currentPeriodEnd &&
        typeof (subscription.currentPeriodEnd as any).toDate === 'function'
      ) {
        subscription.currentPeriodEnd = (subscription.currentPeriodEnd as any).toDate();
      }
      // If it's already a Date object, no need to convert
    }

    // Create user profile in Firestore
    const userProfile: UserProfile = {
      email,
      name: profile.name || '',
      subscription,
      createdAt: new Date(),
      updatedAt: new Date(),
    };

    logger.info('Saving user profile to Firestore', userProfile);
    await setDoc(doc(db, 'users', user.uid), userProfile);
    logger.info('User profile saved successfully');

    return {user, profile: userProfile};
  } catch (error) {
    logger.error('Error registering user', error);
    throw error;
  }
}

export async function loginUser(email: string, password: string) {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    logger.error('Error logging in', error);
    throw error;
  }
}

export async function logoutUser() {
  try {
    logger.info('Signing out from Firebase Auth...');

    // Get auth instance
    const auth = getAuth();

    // Force reload to clear any cached states
    if (auth.currentUser) {
      logger.info(`Current user found, signing out: ${auth.currentUser.uid}`);
    } else {
      logger.info('No current user found in auth object');
    }

    // Sign out from Firebase
    await signOut(auth);

    // Verify logout was successful
    if (auth.currentUser) {
      logger.warn('User still present after signOut, attempting cleanup...');
      // Force signOut again in case of persistence issues
      await signOut(auth);
    } else {
      logger.info('Successfully signed out from Firebase');
    }

    // Clear any locally cached auth data
    try {
      const storageKeys = Object.keys(localStorage);
      const firebaseKeys = storageKeys.filter(key => key.startsWith('firebase:'));

      if (firebaseKeys.length > 0) {
        logger.info('Clearing Firebase localStorage keys', firebaseKeys);
        firebaseKeys.forEach(key => localStorage.removeItem(key));
      }
    } catch (storageError) {
      // Ignore localStorage errors in case of private browsing
      logger.warn('Could not access localStorage', storageError);
    }
  } catch (error) {
    logger.error('Error logging out', error);
    throw error;
  }
}

export function onAuthChange(callback: (user: User | null) => void) {
  return onAuthStateChanged(auth, callback);
}

export async function getCurrentUser(): Promise<User | null> {
  return new Promise(resolve => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      resolve(user);
      unsubscribe();
    });
  });
}

export async function getUserProfile(userId: string): Promise<UserProfile | null> {
  try {
    logger.info(`Getting user profile from Firestore: ${userId}`);
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      const data = userDoc.data();

      // Convert Firestore timestamps to JavaScript Date objects
      const profile: UserProfile = {
        email: data.email,
        name: data.name,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt),
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : new Date(data.updatedAt),
        subscription: {
          ...data.subscription,
          plan: data.subscription.plan,
          status: data.subscription.status,
          currentPeriodEnd: data.subscription?.currentPeriodEnd?.toDate
            ? data.subscription.currentPeriodEnd.toDate()
            : data.subscription?.currentPeriodEnd
            ? new Date(data.subscription.currentPeriodEnd)
            : undefined,
        },
        onboarding: data?.onboarding
          ? {
              ...data.onboarding,
              steps: data.onboarding?.steps || {},
              lastUpdated: data.onboarding?.lastUpdated
                ? data.onboarding?.lastUpdated?.toDate
                  ? data.onboarding.lastUpdated.toDate()
                  : new Date(data.onboarding.lastUpdated)
                : new Date(),
            }
          : {},
      };

      logger.info('User profile retrieved successfully', profile);
      return profile;
    }
    logger.info('User profile not found in Firestore');
    return null;
  } catch (error) {
    logger.error('Error getting user profile', error);
    throw error;
  }
}

export async function updateUserProfile(userId: string, profileData: Partial<UserProfile>) {
  try {
    logger.info('Updating user profile in Firestore', {userId, profileData});

    // Get the user document reference
    const userRef = doc(db, 'users', userId);

    // Add updatedAt timestamp
    const updateData = {
      ...profileData,
      updatedAt: new Date(),
    };

    // Update the document
    await updateDoc(userRef, updateData);

    logger.info('User profile updated successfully');
  } catch (error) {
    logger.error('Error updating user profile', error);
    throw error;
  }
}

export async function updateUserSubscription(userId: string, subscription: Partial<UserSubscription>) {
  try {
    const userRef = doc(db, 'users', userId);

    // Get current user data to update only the subscription fields that changed
    const userDoc = await getDoc(userRef);
    if (!userDoc.exists()) {
      throw new Error('User profile not found');
    }

    const userData = userDoc.data();
    const updatedSubscription = {
      ...userData.subscription,
      ...subscription,
    };

    // Ensure currentPeriodEnd is properly formatted
    if (
      updatedSubscription.currentPeriodEnd &&
      !(updatedSubscription.currentPeriodEnd instanceof Date) &&
      typeof updatedSubscription.currentPeriodEnd === 'object' &&
      'toDate' in updatedSubscription.currentPeriodEnd &&
      typeof (updatedSubscription.currentPeriodEnd as any).toDate === 'function'
    ) {
      updatedSubscription.currentPeriodEnd = (updatedSubscription.currentPeriodEnd as any).toDate();
    }

    await updateDoc(userRef, {
      subscription: updatedSubscription,
      updatedAt: new Date(),
    });

    logger.info('User subscription updated successfully', updatedSubscription);
  } catch (error) {
    logger.error('Error updating user subscription', error);
    throw error;
  }
}

export async function updateUserPassword(user: User, newPassword: string) {
  try {
    await updatePassword(user, newPassword);
    logger.info('User password updated successfully');
  } catch (error) {
    logger.error('Error updating user password', error);
    throw error;
  }
}

export async function updateOnboardingSteps(userId: string, stepId: string, completed: boolean) {
  try {
    logger.info('Updating onboarding step in Firestore', {userId, stepId, completed});

    const userRef = doc(db, 'users', userId);
    const now = new Date();

    // Update the document
    await updateDoc(userRef, {
      [`onboarding.steps.${stepId}`]: {
        completed,
        completedAt: completed ? now : null,
      },
      'onboarding.lastUpdated': now,
      updatedAt: now,
    });

    logger.info('Onboarding step updated successfully');
  } catch (error) {
    logger.error('Error updating onboarding step', error);
    throw error;
  }
}

export interface SessionMetrics {
  userId: string;
  date: Date;
  duration: number; // in seconds
  exercises: {
    type: string;
    duration: number;
  }[];
}

// Create a new session document and initialize click tracking
export async function createSession(userId: string): Promise<string> {
  try {
    logger.info('Creating new session for user:', userId);

    // Create a basic session document in Firestore
    const sessionRef = collection(db, 'sessions');
    const sessionData = {
      userId,
      date: new Date(),
      duration: 0,
      exercises: [],
      clicks: [],
      lastUpdated: new Date(),
    };

    // Add the document to get an ID
    const docRef = await addDoc(sessionRef, sessionData);
    const sessionId = docRef.id;

    logger.info('Session created with ID:', sessionId);

    // Initialize click tracking
    activeClickTracker = initializeClickTracker(db, sessionId);

    return sessionId;
  } catch (error) {
    logger.error('Error creating session:', error);
    throw error;
  }
}

// Get the active click tracker or create a new session if needed
export async function getClickTracker(userId: string): Promise<ClickTracker> {
  // Return existing tracker if available
  if (activeClickTracker) {
    return activeClickTracker;
  }

  // Create a new session and tracker
  await createSession(userId);

  // At this point activeClickTracker should be initialized
  if (!activeClickTracker) {
    throw new Error('Failed to initialize click tracker');
  }

  return activeClickTracker;
}

export async function recordSession(userId: string, metrics: Omit<SessionMetrics, 'userId' | 'date'>) {
  try {
    const sessionRef = collection(db, 'sessions');
    const sessionData: SessionMetrics = {
      userId,
      date: new Date(),
      ...metrics,
    };

    await addDoc(sessionRef, sessionData);
  } catch (error) {
    logger.error('Error recording session', error);
    throw error;
  }
}

export async function getUserSessionMetrics(userId: string) {
  try {
    const sessionsRef = collection(db, 'sessions');
    const q = query(sessionsRef, where('userId', '==', userId));
    const querySnapshot = await getDocs(q);

    const sessions = querySnapshot.docs.map(doc => ({
      ...doc.data(),
      date: doc.data().date.toDate(),
    })) as SessionMetrics[];

    // Calculate metrics
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const thisWeek = new Date(today);
    thisWeek.setDate(today.getDate() - today.getDay());

    const thisMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const thisYear = new Date(today.getFullYear(), 0, 1);

    // Calculate current streak
    let currentStreak = 0;
    let lastSessionDate = null;

    // Get today's date at midnight for comparison
    const todayDate = new Date();
    todayDate.setHours(0, 0, 0, 0);

    // Get yesterday's date at midnight
    const yesterdayDate = new Date(todayDate);
    yesterdayDate.setDate(yesterdayDate.getDate() - 1);

    // Sort sessions by date descending
    const sortedSessions = [...sessions].sort((a, b) => b.date.getTime() - a.date.getTime());

    // Check if there are any sessions
    if (sortedSessions.length > 0) {
      // Get the most recent session date
      const mostRecentSession = new Date(sortedSessions[0].date);
      mostRecentSession.setHours(0, 0, 0, 0);

      // Check if the streak is still active (session today or yesterday)
      const isStreakActive =
        mostRecentSession.getTime() === todayDate.getTime() || mostRecentSession.getTime() === yesterdayDate.getTime();

      if (isStreakActive) {
        // Start counting the streak
        currentStreak = 1;
        lastSessionDate = mostRecentSession;

        // Continue counting consecutive days
        for (let i = 1; i < sortedSessions.length; i++) {
          const sessionDate = new Date(sortedSessions[i].date);
          sessionDate.setHours(0, 0, 0, 0);

          const daysDiff = Math.floor((lastSessionDate.getTime() - sessionDate.getTime()) / (1000 * 60 * 60 * 24));

          if (daysDiff === 1) {
            currentStreak++;
            lastSessionDate = sessionDate;
          } else {
            break; // Streak broken
          }
        }
      }
    }

    // Calculate time in session for different periods
    const calculateTimeInPeriod = (startDate: Date) => {
      return sessions
        .filter(session => session.date >= startDate)
        .reduce((total, session) => total + session.duration, 0);
    };

    return {
      currentStreak,
      timeInSession: {
        today: calculateTimeInPeriod(today),
        thisWeek: calculateTimeInPeriod(thisWeek),
        thisMonth: calculateTimeInPeriod(thisMonth),
        thisYear: calculateTimeInPeriod(thisYear),
      },
      totalSessions: sessions.length,
      lastSessionDate: lastSessionDate,
      sessions: sessions, // Add the actual sessions data
    };
  } catch (error) {
    logger.error('Error getting user session metrics', error);
    throw error;
  }
}

// Email preferences and unsubscribe management
export async function updateEmailPreferences(
  email: string,
  preferences: {
    marketing: boolean;
    beta: boolean;
    productUpdates: boolean;
  }
) {
  try {
    logger.info(`Updating email preferences for ${email}`, preferences);

    // First, check if there's already a record for this email
    const emailPrefsRef = collection(db, 'emailPreferences');
    const q = query(emailPrefsRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      // No existing record, create a new one
      logger.info(`Creating new email preferences record for ${email}`);
      await addDoc(emailPrefsRef, {
        email,
        preferences,
        unsubscribed: false,
        updatedAt: new Date(),
      });
    } else {
      // Update existing record
      const docRef = doc(db, 'emailPreferences', querySnapshot.docs[0].id);
      await updateDoc(docRef, {
        preferences,
        updatedAt: new Date(),
      });
    }

    logger.info(`Email preferences updated successfully for ${email}`);
    return true;
  } catch (error) {
    logger.error('Error updating email preferences', error);
    throw error;
  }
}

export async function unsubscribeEmail(email: string, token: string) {
  try {
    logger.info(`Processing unsubscribe request for ${email}`);

    // Instead of doing AWS SES operations directly in the browser,
    // we'll call the Netlify function we created for this purpose
    const response = await fetch(
      `/.netlify/functions/ses-unsubscribe?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`
    );

    if (!response.ok) {
      logger.error(`Error from unsubscribe API: ${response.status} ${response.statusText}`);
      return {success: false, reason: 'api_error'};
    }

    const result = await response.json();

    // If the API call was successful, track the unsubscribe event
    if (result.success) {
      // Track the unsubscribe event for analytics
      trackEvent('email_unsubscribed', {
        email,
        timestamp: new Date().toISOString(),
      });
    }

    return result;
  } catch (error) {
    logger.error('Error processing unsubscribe request', error);
    return {success: false, reason: 'client_error'};
  }
}

export async function getEmailPreferences(email: string) {
  try {
    logger.info(`Getting email preferences for ${email}`);

    const emailPrefsRef = collection(db, 'emailPreferences');
    const q = query(emailPrefsRef, where('email', '==', email));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      // No preferences record found, return default
      return {
        email,
        preferences: {
          marketing: true,
          beta: true,
          productUpdates: true,
        },
        unsubscribed: false,
      };
    }

    // Return the existing preferences
    const data = querySnapshot.docs[0].data();
    return {
      email: data.email,
      preferences: data.preferences || {
        marketing: !data.unsubscribed,
        beta: !data.unsubscribed,
        productUpdates: !data.unsubscribed,
      },
      unsubscribed: data.unsubscribed || false,
      unsubscribedAt: data.unsubscribedAt?.toDate ? data.unsubscribedAt.toDate() : data.unsubscribedAt,
    };
  } catch (error) {
    logger.error('Error getting email preferences', error);
    throw error;
  }
}

// Analytics event constants
export const Events = {
  LOGIN: 'login',
  SIGNUP: 'sign_up',
  SESSION_START: 'session_start',
  SESSION_COMPLETE: 'session_complete',
  FEATURE_USED: 'feature_used',
  SUBSCRIPTION_CHANGED: 'subscription_changed',
  EXERCISE_STARTED: 'exercise_started',
  EXERCISE_COMPLETED: 'exercise_completed',
  PAGE_VIEW: 'page_view',
  EMAIL_UNSUBSCRIBED: 'email_unsubscribed',
};

// Analytics utility functions
export function trackEvent(eventName: string, eventParams?: Record<string, any>) {
  try {
    logEvent(analytics, eventName, eventParams);
    logger.info(`Tracked event: ${eventName}`, eventParams);
  } catch (error) {
    logger.error(`Failed to track event: ${eventName}`, error);
  }
}

// Analytics function to track user session
export function trackUserSession(userId: string, exerciseType: string) {
  trackEvent(Events.SESSION_START, {
    userId,
    exerciseType,
    timestamp: new Date().toISOString(),
  });
}

// Analytics function to track feature usage
export function trackFeatureUsage(featureName: string, userId?: string) {
  trackEvent(Events.FEATURE_USED, {
    feature: featureName,
    userId,
    timestamp: new Date().toISOString(),
  });
}

// Analytics function to track subscription changes
export function trackSubscriptionChange(userId: string, planFrom: string, planTo: string) {
  trackEvent(Events.SUBSCRIPTION_CHANGED, {
    userId,
    previousPlan: planFrom,
    newPlan: planTo,
    timestamp: new Date().toISOString(),
  });
}
